import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, CheckCircle2, Info } from 'lucide-react';
import {
  SAFE_DEFAULTS_MAX_COMPANIES,
  applyDemoSafeDefaults,
  previewDemoSafeDefaults,
  type DemoSafeDefaultsCompany,
  type DemoSurfaceRef,
} from '../../../api/Navigation/moduleConfigAdmin';
import { Checkbox } from '../../../components/ui/Checkbox';
import type { SurfaceNamer } from './FleetTable';
import { demoSurfaceId } from './useModuleConfigEditor';

/**
 * Review and apply "safe defaults" for one tenant or many (CO-862), inline: no dialog.
 *
 * What the action is, said on the panel because the name invites the wrong reading: it sets STORED
 * switches of demo keys to off (an ON row is switched off, a missing row is added as off). It never
 * deletes, never touches a grant and skips every granted key, so it changes nothing a tenant's users
 * see. Taking a demo surface away from a tenant is a revoke of its grant, on the tenant's own screen.
 *
 * The list is the server's dry run. Every key is ticked; unticking leaves that key of that tenant
 * alone. Apply sends exactly the ticked keys: tenants that share the same ticked keys go in one
 * request, the rest in their own, one after the other. Each tenant is one transaction on the server.
 */

interface SafeDefaultsReviewProps {
  companyIds: number[];
  namer: SurfaceNamer;
  /** At least one tenant's rows were written: whoever shows stored state must read it again. */
  onApplied: () => void;
  /** Cancel, or Done after the outcome was read. */
  onClose: () => void;
  /** Drawn inside another framed panel: no frame of its own (a card inside a card reads as noise). */
  embedded?: boolean;
}

/** One demo key of one tenant: its stored rows that would change, folded together. */
interface ReviewKey {
  id: string;
  ref: DemoSurfaceRef;
  label: string;
  /** True when at least one row exists and is ON; false when the only change is adding the OFF row. */
  switchesOff: boolean;
  /** Module ids of the rows, when they differ from the demo list's own (CO-496 older ids). */
  otherRowModules: string[];
}
interface ReviewCompany {
  company: DemoSafeDefaultsCompany;
  keys: ReviewKey[];
}
interface Outcome {
  appliedCompanies: number;
  failures: { companyId: number; companyName: string; reason: string | null }[];
}
type Phase =
  | { kind: 'loading' }
  | { kind: 'failed'; reason: string | null }
  | { kind: 'review'; companies: ReviewCompany[] }
  | { kind: 'done'; outcome: Outcome };

const buttonBase =
  'inline-flex min-h-8 shrink-0 items-center justify-center whitespace-nowrap rounded-lg border px-3 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50';
const quietButtonClass = `${buttonBase} border-border bg-card text-foreground hover:bg-muted`;
const commitButtonClass = `${buttonBase} border-primary bg-primary text-primary-foreground hover:bg-primary/90`;

const tick = (companyId: number, keyId: string) => `${companyId}/${keyId}`;

function toReview(companies: DemoSafeDefaultsCompany[], namer: SurfaceNamer): ReviewCompany[] {
  return companies.map(company => {
    const keys = new Map<string, ReviewKey>();
    company.changes.forEach(change => {
      const ref: DemoSurfaceRef = {
        surfaceKind: change.surfaceKind,
        surfaceKey: change.surfaceKey,
        // Read by the server for a tab only: its tab group id.
        ...(change.surfaceKind === 'tab' ? { moduleId: change.moduleId } : {}),
      };
      const id = demoSurfaceId(ref);
      const key: ReviewKey =
        keys.get(id) ?? { id, ref, label: namer(change).label, switchesOff: false, otherRowModules: [] };
      if (change.action === 'switchOff') key.switchesOff = true;
      if (change.rowModuleId && change.rowModuleId.toLowerCase() !== change.moduleId.toLowerCase()) {
        key.otherRowModules.push(change.rowModuleId);
      }
      keys.set(id, key);
    });
    return { company, keys: [...keys.values()] };
  });
}

export default function SafeDefaultsReview({ companyIds, namer, onApplied, onClose, embedded = false }: SafeDefaultsReviewProps) {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  /** Keys the operator unticked; everything else is sent. */
  const [unticked, setUnticked] = useState<ReadonlySet<string>>(new Set());
  const [applying, setApplying] = useState(false);
  // The selection this panel was opened for. A new selection is a new panel (the caller closes it first).
  const idsKey = companyIds.join(',');

  useEffect(() => {
    let cancelled = false;
    setPhase({ kind: 'loading' });
    setUnticked(new Set());
    (async () => {
      const result = await previewDemoSafeDefaults({ companyIds: idsKey.split(',').map(Number) });
      if (cancelled) return;
      setPhase(
        result.ok
          ? { kind: 'review', companies: toReview(result.value.companies, namer) }
          : { kind: 'failed', reason: result.reason }
      );
    })();
    return () => {
      cancelled = true;
    };
    // `namer` only names the rows; a language switch does not warrant a second dry run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey]);

  const companies = useMemo(() => (phase.kind === 'review' ? phase.companies : []), [phase]);
  const withChanges = companies.filter(entry => entry.keys.length > 0);
  const tickedCount = withChanges.reduce(
    (sum, entry) => sum + entry.keys.filter(key => !unticked.has(tick(entry.company.companyId, key.id))).length,
    0
  );

  const setTicks = (ids: string[], on: boolean) =>
    setUnticked(previous => {
      const next = new Set(previous);
      ids.forEach(id => (on ? next.delete(id) : next.add(id)));
      return next;
    });

  const apply = async () => {
    if (applying || tickedCount === 0) return;
    setApplying(true);

    // Tenants with the same ticked keys share a request; the keys are always named, never "the whole
    // list", so nothing is written that was not on screen.
    type Batch = { surfaces: DemoSurfaceRef[]; companies: DemoSafeDefaultsCompany[] };
    const batches = new Map<string, Batch>();
    withChanges.forEach(entry => {
      const keys = entry.keys.filter(key => !unticked.has(tick(entry.company.companyId, key.id)));
      if (keys.length === 0) return;
      const signature = keys.map(key => key.id).sort().join('\n');
      const batch: Batch = batches.get(signature) ?? { surfaces: keys.map(key => key.ref), companies: [] };
      batch.companies.push(entry.company);
      batches.set(signature, batch);
    });

    const outcome: Outcome = { appliedCompanies: 0, failures: [] };
    for (const batch of batches.values()) {
      for (let start = 0; start < batch.companies.length; start += SAFE_DEFAULTS_MAX_COMPANIES) {
        const slice = batch.companies.slice(start, start + SAFE_DEFAULTS_MAX_COMPANIES);
        // Sequential on purpose: one tenant, one transaction, one broadcast at a time.
        const result = await applyDemoSafeDefaults({
          companyIds: slice.map(company => company.companyId),
          surfaces: batch.surfaces,
        });
        if (!result.ok) {
          // No readable answer for this request: its tenants may or may not have been written.
          slice.forEach(company =>
            outcome.failures.push({ companyId: company.companyId, companyName: company.companyName, reason: result.reason })
          );
          continue;
        }
        result.value.companies.forEach(company => {
          if (company.error) {
            outcome.failures.push({ companyId: company.companyId, companyName: company.companyName, reason: company.error });
          } else if (company.applied) {
            outcome.appliedCompanies += 1;
          }
        });
      }
    }

    setApplying(false);
    setPhase({ kind: 'done', outcome });
    // Also after a request that got no answer: its rows may have been written.
    if (outcome.appliedCompanies > 0 || outcome.failures.length > 0) onApplied();
  };

  return (
    <section
      data-testid="module-config-tidy"
      data-phase={phase.kind}
      aria-labelledby="module-config-tidy-heading"
      aria-busy={phase.kind === 'loading' || applying}
      className={embedded ? '' : 'mb-3 overflow-hidden rounded-lg border border-border bg-card'}
    >
      <div className="space-y-1 border-b border-border bg-muted px-4 py-2.5">
        <h3 id="module-config-tidy-heading" className="text-sm font-medium text-foreground">
          {t('moduleConfig.fleet.bulk.heading', { count: companyIds.length })}
        </h3>
        <p className="flex max-w-[75ch] items-start gap-1.5 text-xs leading-relaxed text-muted-foreground">
          <Info aria-hidden="true" className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {t('moduleConfig.fleet.bulk.explain')}
        </p>
      </div>

      {phase.kind === 'loading' && (
        <p role="status" className="px-4 py-6 text-center text-sm text-muted-foreground">
          {t('moduleConfig.fleet.bulk.loading')}
        </p>
      )}

      {phase.kind === 'failed' && (
        <div role="alert" data-testid="module-config-tidy-error" className="flex flex-wrap items-center gap-3 px-4 py-3 text-sm text-destructive">
          <AlertCircle aria-hidden="true" className="h-4 w-4 shrink-0" />
          <span className="min-w-0 flex-1">
            {phase.reason
              ? t('moduleConfig.fleet.bulk.previewFailedWithReason', { reason: phase.reason })
              : t('moduleConfig.fleet.bulk.previewFailed')}
          </span>
          <button type="button" className={quietButtonClass} onClick={onClose}>
            {t('moduleConfig.fleet.bulk.cancel')}
          </button>
        </div>
      )}

      {phase.kind === 'review' && (
        <>
          {withChanges.length === 0 ? (
            <p data-testid="module-config-tidy-nothing" className="px-4 py-6 text-center text-sm text-muted-foreground">
              {t('moduleConfig.fleet.bulk.nothing')}
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {withChanges.map(({ company, keys }) => {
                const ids = keys.map(key => tick(company.companyId, key.id));
                const allOn = ids.every(id => !unticked.has(id));
                return (
                  <li key={company.companyId} data-testid={`module-config-tidy-company-${company.companyId}`} className="px-4 py-2.5">
                    <Checkbox
                      checked={allOn}
                      disabled={applying}
                      onChange={() => setTicks(ids, !allOn)}
                      label={t('moduleConfig.fleet.bulk.tenantLine', {
                        name: company.companyName,
                        id: company.companyId,
                        count: keys.length,
                      })}
                      data-testid={`module-config-tidy-company-check-${company.companyId}`}
                    />
                    <ul className="mt-2 space-y-1.5 ps-6">
                      {keys.map(key => {
                        const id = tick(company.companyId, key.id);
                        return (
                          <li key={key.id} className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                            <Checkbox
                              checked={!unticked.has(id)}
                              disabled={applying}
                              onChange={() => setTicks([id], unticked.has(id))}
                              aria-label={t('moduleConfig.fleet.bulk.keyLabel', { name: key.label, company: company.companyName })}
                              data-testid={`module-config-tidy-key-${company.companyId}-${key.id}`}
                            />
                            <span className="min-w-0 truncate text-sm text-foreground">{key.label}</span>
                            {key.label !== key.ref.surfaceKey && (
                              // A route or a storage key, so it reads left to right in every language.
                              <span dir="ltr" className="min-w-0 truncate font-mono text-xs text-muted-foreground">
                                {key.ref.surfaceKey}
                              </span>
                            )}
                            <span className="shrink-0 text-xs text-muted-foreground">
                              {t(key.switchesOff ? 'moduleConfig.fleet.bulk.switchOff' : 'moduleConfig.fleet.bulk.insertOff')}
                            </span>
                            {key.otherRowModules.length > 0 && (
                              <span className="shrink-0 text-xs text-muted-foreground">
                                {t('moduleConfig.fleet.bulk.storedUnder')}{' '}
                                <span dir="ltr" className="font-mono">{key.otherRowModules.join(', ')}</span>
                              </span>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                    {company.notTouched.length > 0 && (
                      <p data-testid={`module-config-tidy-not-touched-${company.companyId}`} className="mt-2 ps-6 text-xs text-muted-foreground">
                        {t('moduleConfig.fleet.bulk.notTouched', {
                          names: company.notTouched.map(surface => namer(surface).label).join(', '),
                        })}
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {companies.length > withChanges.length && withChanges.length > 0 && (
            <p className="border-t border-border px-4 py-2 text-xs text-muted-foreground">
              {t('moduleConfig.fleet.bulk.unchangedTenants', { count: companies.length - withChanges.length })}
            </p>
          )}
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border px-4 py-2.5">
            <button type="button" className={quietButtonClass} disabled={applying} data-testid="module-config-tidy-cancel" onClick={onClose}>
              {t('moduleConfig.fleet.bulk.cancel')}
            </button>
            {withChanges.length > 0 && (
              <button
                type="button"
                className={commitButtonClass}
                disabled={applying || tickedCount === 0}
                aria-busy={applying}
                data-testid="module-config-tidy-commit"
                onClick={apply}
              >
                {t('moduleConfig.fleet.bulk.commit', { count: tickedCount })}
              </button>
            )}
          </div>
        </>
      )}

      {phase.kind === 'done' && (
        <div data-testid="module-config-tidy-done" className="space-y-2 px-4 py-3">
          {phase.outcome.appliedCompanies > 0 && (
            <p role="status" className="flex items-start gap-2 text-sm text-success-strong">
              <CheckCircle2 aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
              {t('moduleConfig.fleet.bulk.done', { count: phase.outcome.appliedCompanies })}
            </p>
          )}
          {phase.outcome.appliedCompanies === 0 && phase.outcome.failures.length === 0 && (
            <p role="status" className="text-sm text-muted-foreground">{t('moduleConfig.fleet.bulk.doneNothing')}</p>
          )}
          {phase.outcome.failures.length > 0 && (
            <div role="alert" className="space-y-1 text-sm text-destructive">
              <p className="flex items-start gap-2 font-medium">
                <AlertCircle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
                {t('moduleConfig.fleet.bulk.failed', { count: phase.outcome.failures.length })}
              </p>
              <ul className="space-y-0.5 ps-6 text-xs">
                {phase.outcome.failures.map(failure => (
                  <li key={failure.companyId}>
                    {failure.companyName} <span dir="ltr" className="tabular-nums">#{failure.companyId}</span>
                    {failure.reason ? `: ${failure.reason}` : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="flex justify-end">
            <button type="button" className={quietButtonClass} data-testid="module-config-tidy-close" onClick={onClose}>
              {t('moduleConfig.fleet.bulk.close')}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
